/**
 * AiAssistant — native AI Marketing Assistant (Phase 1).
 *
 * Sections:
 *   - Product profile editor (feeds every generation)
 *   - Strategy generator (audience segments, campaign angles, ad copy)
 *   - SEO/AEO generator (keywords, titles, meta descriptions, topics, FAQs,
 *     internal links, AEO answer blocks, llms.txt) with review+click "Apply"
 *     buttons that push into the existing SEO file editor endpoints.
 *
 * Meta-dependent performance/optimization analysis lands in Phase 2.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { clearConfigCache } from "@/lib/config";
import {
  Sparkles, Loader2, Save, Megaphone, Search, Wand2, Check, Copy,
} from "lucide-react";

const LIST_FIELDS = [
  { key: "value_props", label: "Value propositions" },
  { key: "icp_segments", label: "Ideal customer segments" },
  { key: "geos", label: "Target geographies" },
  { key: "competitors", label: "Competitors / alternatives" },
];
const TEXT_FIELDS = [
  { key: "product_name", label: "Product name", area: false },
  { key: "one_liner", label: "One-liner", area: true },
  { key: "price_points", label: "Pricing", area: false },
  { key: "primary_conversion", label: "Primary conversion path", area: false },
  { key: "brand_tone", label: "Brand tone", area: false },
  { key: "website_url", label: "Website URL", area: false },
  { key: "extra_notes", label: "Additional context (optional)", area: true },
];

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (txt) => (txt || "").split("\n").map((s) => s.trim()).filter(Boolean);

export default function AiAssistant() {
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/marketing/profile")
      .then(({ data }) => setProfile(data.profile))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load profile"))
      .finally(() => setLoading(false));
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.put("/admin/marketing/profile", profile);
      setProfile(data.profile);
      toast.success("Profile saved — future generations use this.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading || !profile) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  return (
    <div className="space-y-8" data-testid="ai-assistant">
      <div className="rounded-lg border border-teal-200 dark:border-teal-500/30 bg-teal-50 dark:bg-teal-500/10 p-4 flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-teal-700 dark:text-teal-400 mt-0.5 shrink-0" />
        <div className="text-sm text-teal-900 dark:text-teal-100">
          <p className="font-semibold">AI Marketing Assistant</p>
          <p className="mt-1 text-teal-900/80 dark:text-teal-100/80">
            Keep the profile below accurate — every recommendation is generated from it. Performance
            &amp; optimization analysis unlocks once Meta Ads is connected.
          </p>
        </div>
      </div>

      {/* Profile editor */}
      <section data-testid="marketing-profile-editor">
        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Product profile</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
          {TEXT_FIELDS.map((f) => (
            <div key={f.key} className={f.area ? "md:col-span-2" : ""}>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">{f.label}</label>
              {f.area ? (
                <textarea
                  value={profile[f.key] ?? ""}
                  onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                  data-testid={`profile-${f.key}`}
                  className="w-full h-20 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
                />
              ) : (
                <input
                  value={profile[f.key] ?? ""}
                  onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                  data-testid={`profile-${f.key}`}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
                />
              )}
            </div>
          ))}
          {LIST_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400 mb-1.5">
                {f.label} <span className="normal-case text-slate-400">(one per line)</span>
              </label>
              <textarea
                value={toLines(profile[f.key])}
                onChange={(e) => setProfile({ ...profile, [f.key]: fromLines(e.target.value) })}
                data-testid={`profile-${f.key}`}
                className="w-full h-24 px-3 py-2 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-600"
              />
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={saveProfile}
            disabled={savingProfile}
            data-testid="profile-save"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
          >
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save profile
          </button>
        </div>
      </section>

      <StrategyGenerator />
      <SeoGenerator />
    </div>
  );
}

// ---------------------------------------------------------------------------
function GenerateButton({ onClick, loading, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      data-testid={testid}
      className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 disabled:opacity-50 transition-colors"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
      {children}
    </button>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{title}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors shrink-0"
      title="Copy"
    >
      {done ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Kick off an async generation job and poll until ready/failed.
async function runJob(endpoint) {
  const { data } = await api.post(endpoint);
  const jobId = data.job_id;
  const started = Date.now();
  // Poll every 3s, up to 4 minutes.
  while (Date.now() - started < 240000) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data: job } = await api.get(`/admin/marketing/ai/job/${jobId}`);
    if (job.status === "ready") return job.recommendation;
    if (job.status === "failed") throw new Error(job.error || "Generation failed");
  }
  throw new Error("Generation timed out — please try again.");
}

// ---------------------------------------------------------------------------
function StrategyGenerator() {
  const [out, setOut] = useState(null);
  const [recId, setRecId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/marketing/ai/history", { params: { type: "strategy", limit: 1 } })
      .then(({ data }) => { const r = data.recommendations?.[0]; if (r) { setOut(r.output); setRecId(r.id); } })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const rec = await runJob("/admin/marketing/ai/strategy");
      setOut(rec.output);
      setRecId(rec.id);
      toast.success("Strategy generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const updateAdCopy = (i, key, val) =>
    setOut((prev) => ({ ...prev, ad_copy: prev.ad_copy.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)) }));

  const saveEdits = async () => {
    if (!recId) { toast.error("Generate a strategy first, then edit and save."); return; }
    setSaving(true);
    try {
      await api.put(`/admin/marketing/ai/recommendation/${recId}`, { output: out });
      toast.success("Ad copy saved — your edits will stick until you regenerate.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="strategy-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">Meta Ads strategy</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">Audience segments, campaign angles &amp; ready-to-use ad copy.</p>
        </div>
        <GenerateButton onClick={generate} loading={loading} icon={Megaphone} testid="generate-strategy">
          {out ? "Regenerate" : "Generate strategy"}
        </GenerateButton>
      </div>
      {loading && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Generating with Claude — this can take up to 2 minutes.</p>}

      {out && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title="Audience segments">
            <ul className="space-y-3">
              {(out.audience_segments || []).map((a, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium text-slate-900 dark:text-slate-50">{a.name}</p>
                  <p className="text-slate-600 dark:text-slate-300">{a.description}</p>
                  {a.meta_targeting && (
                    <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">
                      <span className="font-mono">interests:</span> {(a.meta_targeting.interests || []).join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Campaign angles">
            <ul className="space-y-3">
              {(out.campaign_angles || []).map((c, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium text-slate-900 dark:text-slate-50">{c.angle}</p>
                  <p className="text-teal-700 dark:text-teal-400 italic">&ldquo;{c.hook}&rdquo;</p>
                  <p className="text-slate-500 dark:text-slate-400 text-[12px]">{c.rationale}</p>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Ad copy variations (edit, then save)">
            <ul className="space-y-3">
              {(out.ad_copy || []).map((a, i) => (
                <li key={i} className="border-b border-slate-100 dark:border-slate-800 last:border-0 pb-3 last:pb-0 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      value={a.headline || ""}
                      onChange={(e) => updateAdCopy(i, "headline", e.target.value)}
                      data-testid={`adcopy-headline-${i}`}
                      placeholder="Headline (≤40 chars)"
                      className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                    />
                    <span className="text-[10px] text-slate-400 font-mono w-8 text-right">{(a.headline || "").length}c</span>
                    <CopyBtn text={`${a.headline}\n${a.primary_text}\n${a.description || ""}`} />
                  </div>
                  <textarea
                    value={a.primary_text || ""}
                    onChange={(e) => updateAdCopy(i, "primary_text", e.target.value)}
                    data-testid={`adcopy-primary-${i}`}
                    rows={2}
                    placeholder="Primary text"
                    className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      value={a.description || ""}
                      onChange={(e) => updateAdCopy(i, "description", e.target.value)}
                      data-testid={`adcopy-desc-${i}`}
                      placeholder="Description (≤30 chars)"
                      className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                    />
                    <input
                      value={a.cta || ""}
                      onChange={(e) => updateAdCopy(i, "cta", e.target.value)}
                      data-testid={`adcopy-cta-${i}`}
                      placeholder="CTA"
                      className="w-32 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                    />
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveEdits}
                disabled={saving || !recId}
                data-testid="adcopy-save"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-xs font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save ad copy
              </button>
            </div>
          </Card>
          <Card title="Interests & detailed targeting to test">
            <div className="flex flex-wrap gap-2">
              {(out.keywords_interests || []).map((k, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200">{k}</span>
              ))}
            </div>
            {out.notes?.length > 0 && (
              <ul className="mt-3 space-y-1.5 list-disc list-inside text-[12px] text-slate-500 dark:text-slate-400">
                {out.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </Card>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
function SeoGenerator() {
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState("");
  const [savingFaqs, setSavingFaqs] = useState(false);

  // Edit AI output in place before applying/saving.
  const updateStr = (field, i, val) => setOut((prev) => ({ ...prev, [field]: prev[field].map((x, idx) => (idx === i ? val : x)) }));
  const updateFaq = (i, key, val) => setOut((prev) => ({ ...prev, faqs: prev.faqs.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)) }));

  useEffect(() => {
    api.get("/admin/marketing/ai/history", { params: { type: "seo", limit: 1 } })
      .then(({ data }) => { if (data.recommendations?.[0]) setOut(data.recommendations[0].output); })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const rec = await runJob("/admin/marketing/ai/seo");
      setOut(rec.output);
      toast.success("SEO/AEO recommendations generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const applyMetaTag = async (key, value, id) => {
    setApplying(id);
    try {
      await api.put("/admin/marketing/meta-tags", { tags: { [key]: value } });
      toast.success(`${key === "title" ? "Page title" : "Meta description"} applied to index.html`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying("");
    }
  };

  const applyLlms = async () => {
    setApplying("llms");
    try {
      await api.put("/admin/marketing/seo-files/llms.txt", { content: out.llms_txt_suggestion });
      toast.success("llms.txt updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying("");
    }
  };

  const saveFaqs = async () => {
    const items = (out.faqs || [])
      .map((f) => ({ question: (f.question || "").trim(), answer: (f.answer || "").trim() }))
      .filter((f) => f.question && f.answer);
    if (!items.length) { toast.error("Add at least one complete question and answer."); return; }
    setSavingFaqs(true);
    try {
      const { data } = await api.put("/admin/marketing/faqs", { items });
      clearConfigCache();
      toast.success(`Saved ${data.count} FAQ${data.count === 1 ? "" : "s"} — live on /faq, Pricing & search schema.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingFaqs(false);
    }
  };

  const ApplyBtn = ({ onClick, id, children }) => (
    <button
      onClick={onClick}
      disabled={!!applying}
      data-testid={`apply-${id}`}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 dark:text-teal-400 hover:underline disabled:opacity-50 shrink-0"
    >
      {applying === id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} {children}
    </button>
  );

  return (
    <section data-testid="seo-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">SEO &amp; AEO content engine</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300">Keywords, titles, meta descriptions, FAQs &amp; AI-answer blocks. Review, then apply.</p>
        </div>
        <GenerateButton onClick={generate} loading={loading} icon={Search} testid="generate-seo">
          {out ? "Regenerate" : "Generate SEO/AEO"}
        </GenerateButton>
      </div>
      {loading && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Generating with Claude — this can take up to 2 minutes.</p>}

      {out && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card title="Keyword ideas">
            <ul className="space-y-2 text-sm">
              {(out.keyword_ideas || []).map((k, i) => (
                <li key={i} className="flex items-start justify-between gap-2">
                  <span className="text-slate-800 dark:text-slate-100">{k.keyword}</span>
                  <span className="text-[11px] text-slate-400 font-mono whitespace-nowrap">{k.intent}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Page titles (edit, then apply)">
            <ul className="space-y-2 text-sm">
              {(out.page_titles || []).map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <input
                    value={t}
                    onChange={(e) => updateStr("page_titles", i, e.target.value)}
                    data-testid={`title-input-${i}`}
                    className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                  <span className="text-[10px] text-slate-400 font-mono pt-2 w-8 text-right">{t.length}c</span>
                  <div className="pt-0.5"><ApplyBtn id={`title-${i}`} onClick={() => applyMetaTag("title", t, `title-${i}`)}>apply</ApplyBtn></div>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Meta descriptions (edit, then apply)">
            <ul className="space-y-2 text-sm">
              {(out.meta_descriptions || []).map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <textarea
                    value={d}
                    onChange={(e) => updateStr("meta_descriptions", i, e.target.value)}
                    data-testid={`desc-input-${i}`}
                    rows={2}
                    className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-100 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                  <span className="text-[10px] text-slate-400 font-mono pt-2 w-8 text-right">{d.length}c</span>
                  <div className="pt-0.5"><ApplyBtn id={`desc-${i}`} onClick={() => applyMetaTag("description", d, `desc-${i}`)}>apply</ApplyBtn></div>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Article topics">
            <ul className="space-y-2.5 text-sm">
              {(out.article_topics || []).map((a, i) => (
                <li key={i}>
                  <p className="font-medium text-slate-900 dark:text-slate-50">{a.title}</p>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400">{a.angle} · <span className="font-mono">{a.target_keyword}</span></p>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="FAQs (edit, then save to your site)">
            <ul className="space-y-3 text-sm">
              {(out.faqs || []).map((f, i) => (
                <li key={i} className="rounded-md border border-slate-200 dark:border-slate-800 p-2.5">
                  <input
                    value={f.question}
                    onChange={(e) => updateFaq(i, "question", e.target.value)}
                    data-testid={`ai-faq-question-${i}`}
                    placeholder="Question"
                    className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-50 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                  <textarea
                    value={f.answer}
                    onChange={(e) => updateFaq(i, "answer", e.target.value)}
                    data-testid={`ai-faq-answer-${i}`}
                    placeholder="Answer"
                    rows={2}
                    className="mt-1.5 w-full px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-600 dark:text-slate-300 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-end">
              <button
                onClick={saveFaqs}
                disabled={savingFaqs}
                data-testid="ai-faq-save"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-xs font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 transition-colors"
              >
                {savingFaqs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save FAQs to site
              </button>
            </div>
          </Card>
          <Card title="AEO answer blocks">
            <ul className="space-y-2.5 text-sm">
              {(out.aeo_answer_blocks || []).map((a, i) => (
                <li key={i} className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-50">{a.question}</p>
                    <p className="text-slate-600 dark:text-slate-300">{a.answer}</p>
                  </div>
                  <CopyBtn text={`${a.question}\n${a.answer}`} />
                </li>
              ))}
            </ul>
          </Card>
          {out.internal_links?.length > 0 && (
            <Card title="Internal linking">
              <ul className="space-y-1.5 text-sm">
                {out.internal_links.map((l, i) => (
                  <li key={i} className="text-slate-700 dark:text-slate-200">
                    <span className="font-mono text-[12px] text-slate-500">{l.from} → {l.to}</span> · &ldquo;{l.anchor}&rdquo;
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {out.llms_txt_suggestion && (
            <Card title="llms.txt (AEO file)">
              <div className="flex items-center justify-end mb-2">
                <ApplyBtn id="llms" onClick={applyLlms}>Apply to llms.txt</ApplyBtn>
              </div>
              <pre className="whitespace-pre-wrap text-[12px] text-slate-700 dark:text-slate-200 font-mono max-h-64 overflow-y-auto bg-slate-50 dark:bg-slate-950 rounded p-3">{out.llms_txt_suggestion}</pre>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}
